import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { varinymAPI } from '../../lib/varinym-api';
import { CurrentPlanCard } from './CurrentPlanCard';
import { UsageCard } from './UsageCard';
import { InvoiceHistoryCard } from './InvoiceHistoryCard';
import { PaymentMethodCard } from './PaymentMethodCard';
import { CancellationModal } from './CancellationModal';

export function Billing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Fetch subscription data
  const { data: subscription, isLoading: isLoadingSubscription } = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () => varinymAPI.getSubscription(),
    enabled: !!user,
  });

  // Fetch usage data
  const { data: usage, isLoading: isLoadingUsage } = useQuery({
    queryKey: ['billing', 'usage'],
    queryFn: () => varinymAPI.getUsage(),
    enabled: !!user && subscription?.tier !== 'developer',
  });

  // Fetch invoices
  const { data: invoicesData, isLoading: isLoadingInvoices } = useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: () => varinymAPI.getInvoices(),
    enabled: !!user && subscription?.tier !== 'developer',
  });

  // Portal URL mutation
  const portalMutation = useMutation({
    mutationFn: () => varinymAPI.getPortalUrl(),
    onSuccess: (data) => {
      // Redirect to Paddle portal
      window.location.href = data.portal_url;
    },
    onError: (err: Error) => {
      alert(`Failed to open payment portal: ${err.message}`);
    },
  });

  // Cancel subscription mutation
  const cancelMutation = useMutation({
    mutationFn: () => varinymAPI.cancelSubscription(),
    onSuccess: () => {
      // Refresh subscription data
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
      queryClient.invalidateQueries({ queryKey: ['me'] });
      setShowCancelModal(false);
      alert('Your subscription has been canceled successfully.');
    },
    onError: (err: Error) => {
      alert(`Failed to cancel subscription: ${err.message}`);
    },
  });

  const handleUpgrade = () => {
    navigate('/billing/plans');
  };

  const handleManagePayment = () => {
    portalMutation.mutate();
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleCancelConfirm = () => {
    cancelMutation.mutate();
  };

  if (isLoadingSubscription) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Loading billing information...</p>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Failed to load billing information</p>
        <p className="text-sm text-muted-foreground mt-2">Please try refreshing the page</p>
      </div>
    );
  }

  const isDeveloper = subscription.tier === 'developer';

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <CurrentPlanCard
        subscription={subscription}
        onUpgrade={handleUpgrade}
        onManagePayment={handleManagePayment}
        onCancel={handleCancelClick}
        isLoadingPortal={portalMutation.isPending}
      />

      {/* Usage Tracking (only for paid tiers) */}
      {!isDeveloper && <UsageCard usage={usage} isLoading={isLoadingUsage} />}

      {/* Payment Method (only for paid tiers) */}
      {!isDeveloper && (
        <PaymentMethodCard
          onManagePayment={handleManagePayment}
          isLoading={portalMutation.isPending}
        />
      )}

      {/* Invoice History (only for paid tiers) */}
      {!isDeveloper && (
        <InvoiceHistoryCard
          invoices={invoicesData?.invoices || []}
          isLoading={isLoadingInvoices}
        />
      )}

      {/* Free Tier Upgrade Prompt */}
      {isDeveloper && (
        <div className="bg-muted border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-2">Upgrade to unlock more features</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Get higher limits, premium features, and priority support with a paid plan.
          </p>
          <ul className="space-y-2 mb-6 text-sm">
            <li className="flex items-center gap-2">
              <span className="text-success">✓</span>
              <span>Higher API call limits (up to 1M+/month)</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-success">✓</span>
              <span>More identities and custom contexts</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-success">✓</span>
              <span>Advanced features (webhooks, custom domains, SSO)</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-success">✓</span>
              <span>Priority support and SLA guarantees</span>
            </li>
          </ul>
          <button
            onClick={handleUpgrade}
            className="bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-md font-medium transition-colors"
          >
            View Plans & Pricing
          </button>
        </div>
      )}

      {/* Cancellation Modal */}
      <CancellationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelConfirm}
        isLoading={cancelMutation.isPending}
        periodEnd={subscription.current_period.end}
      />
    </div>
  );
}
